import { client } from ".";
import { getRateForOpp } from "./calculateVentas";

/**
 * Carga los costos de intermediación que REDUCEN la base de comisión (`reduce_base_comision=true`)
 * para un país, agregados por venta. Devuelve un Map keyed por `${venta_tipo}:${venta_ref_id}`
 * con la SUMA del take en USD por venta (todas las capas con el flag: take del exchange Place +
 * costos de giro Citibank/Santander). Insumo de FASE 6 — el take reduce el neto de intermediación
 * sobre el que se calculan comisión comercial y comisión de edificios (ver ARQUITECTURA 7.18 / 9.30).
 *
 * Unidades: `monto_real`/`monto_teorico` están en USD; se prioriza `monto_real ?? monto_teorico`
 * (en esta etapa sólo existe `monto_real`). La conversión a moneda LOCAL NO se hace acá (depende
 * del TC de cada venta) — la resuelve `getTakeLocal` con el rate de la venta (getRateForOpp),
 * el MISMO que usa el importe. País-scopeado, una sola query. Espeja el patrón de
 * `cargarSnapshotComision` (comisionSnapshot.js).
 *
 * Aún NO lo usa ningún motor por defecto (los motores aceptan `takeLocal=0` → no-op); se cablea
 * en los consumidores en el Commit 2 de FASE 6.
 *
 * @param {string} pais  'Chile' | 'Perú' (convención de costos_intermediacion.pais)
 * @returns {Promise<Map<string, number>>}  `${venta_tipo}:${venta_ref_id}` → take USD por venta
 */
export const cargarCostosIntermediacion = async (pais) => {
  const { data, error } = await client
    .from("costos_intermediacion")
    .select("venta_tipo, venta_ref_id, monto_real, monto_teorico, reduce_base_comision")
    .eq("pais", pais)
    .eq("reduce_base_comision", true);
  if (error) throw error;
  const map = new Map();
  for (const r of data || []) {
    if (r.venta_tipo == null || r.venta_ref_id == null) continue;
    const montoUsd = Number(r.monto_real ?? r.monto_teorico ?? 0);
    if (!montoUsd) continue;
    const key = `${r.venta_tipo}:${r.venta_ref_id}`;
    map.set(key, (map.get(key) || 0) + montoUsd);
  }
  return map;
};

/**
 * Take (costos con `reduce_base_comision`) de una venta, convertido a moneda LOCAL con el MISMO
 * rate que la venta usa para su importe (`getRateForOpp` → TC de la fecha de factura). Devuelve 0
 * si no hay map o no hay match. Pensado para llamarse en el call site, junto al armado de `local`
 * (oppEnLocal), y pasar el escalar resultante a los motores de base (`calcularComisionEdificiosTotal`
 * y `calculateSalesCommission`) como `takeLocal`.
 *
 * Se le pasa la venta ORIGINAL (con `moneda_dolar`/fechas), NO la versión `oppEnLocal` (que ya
 * perdió `moneda_dolar`). `getRateForOpp` devuelve 1 si la venta no es en USD, así que la
 * multiplicación es segura para cualquier venta (las que tienen costos son DTE 110 en USD).
 *
 * @param {Object}   opp        venta ORIGINAL (con moneda_dolar/facturafecha en USD, pre-oppEnLocal)
 * @param {string}   ventaTipo  'sales' | 'sales_contratos'
 * @param {number}   ventaRefId id de la venta (sales.id / sales_contratos.id)
 * @param {Map}      costosMap  resultado de `cargarCostosIntermediacion` (USD por venta)
 * @param {Function} getDolarFn función de TC (bound por país)
 * @param {number}   [fallback=1]
 * @returns {number} take en moneda local (0 si no aplica)
 */
export const getTakeLocal = (opp, ventaTipo, ventaRefId, costosMap, getDolarFn, fallback = 1) => {
  if (!costosMap || ventaRefId == null) return 0;
  const takeUsd = costosMap.get(`${ventaTipo}:${ventaRefId}`) || 0;
  if (!takeUsd) return 0;
  return takeUsd * getRateForOpp(opp, getDolarFn, fallback);
};
